/**
 * TerminalCustomerAccount
 * Full-screen terminal overlay to look up any customer's account.
 *
 * Flow:
 *   1. PIN gate — Manager or Admin PIN
 *   2. Customer search — by name (ALL customers, not just those with balance)
 *   3. Account view — 3 tabs: Open Invoices | History | Payments
 *      Each tab has 58mm thermal print + full-page print options.
 *      "Collect Payment" button on Open Invoices tab hands off to
 *      TerminalCollectPayment-style inline flow.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Search, Loader2, ArrowLeft, User, Printer,
  AlertTriangle, ChevronRight, CheckCircle2, Clock,
  Wallet, Send
} from 'lucide-react';
import PrintBridge from '../../lib/PrintBridge';
import PrintEngine from '../../lib/PrintEngine';
import SendToPrintModal from '../../components/SendToPrintModal';
import TerminalPrintExtrasSheet, { hasExtras } from './TerminalPrintExtrasSheet';
import { toast } from 'sonner';

const fmtPHP = v => `₱${(parseFloat(v) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = iso => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' });
  } catch { return iso?.slice(0, 10) || '—'; }
};

const PERIOD_OPTIONS = [
  { value: '7d',  label: 'Last 7 days'  },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: '3 months'     },
  { value: 'all', label: 'All time'     },
];

// ── Step 1: PIN Gate ───────────────────────────────────────────────────────────
function PinGate({ api, branchId, onVerified }) {
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 120); }, []);

  const verify = async () => {
    if (!pin || pin.length < 4) { setError('Enter a valid PIN or 6-digit TOTP'); return; }
    setVerifying(true); setError('');
    try {
      const res = await api.post('/verify/verify-pin-action', {
        pin,
        action: 'receive_customer_payment', // reuses the same gate
        branch_id: branchId,
      });
      if (res.data?.verified || res.data?.verifier_id) {
        onVerified({ verifierName: res.data.verifier_name || 'Manager', pin });
      } else {
        setError('PIN not accepted — try manager or admin PIN');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid PIN');
    } finally {
      setVerifying(false);
      setPin('');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-12 space-y-5">
      <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
        <User size={26} className="text-blue-700" />
      </div>
      <div className="text-center">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Customer Account</h2>
        <p className="text-xs text-slate-500 mt-1">Enter Manager or Admin PIN to view account details</p>
      </div>
      <input
        ref={inputRef}
        type="password"
        inputMode="numeric"
        maxLength={6}
        value={pin}
        onChange={e => { setPin(e.target.value); setError(''); }}
        onKeyDown={e => e.key === 'Enter' && verify()}
        placeholder="PIN / TOTP"
        className="h-12 w-full max-w-[200px] text-center font-mono text-xl tracking-widest rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="cust-account-pin-input"
        autoComplete="one-time-code"
      />
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1.5">
          <AlertTriangle size={12} /> {error}
        </p>
      )}
      <button
        onClick={verify}
        disabled={verifying || pin.length < 4}
        className="w-full max-w-[200px] py-3 rounded-xl bg-blue-700 text-white font-semibold text-sm disabled:opacity-50 active:scale-95 transition-all flex items-center justify-center gap-2"
        data-testid="cust-account-pin-confirm"
      >
        {verifying ? <Loader2 size={14} className="animate-spin" /> : null}
        {verifying ? 'Verifying…' : 'Continue'}
      </button>
    </div>
  );
}

// ── Step 2: Customer Search ────────────────────────────────────────────────────
function CustomerSearch({ api, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const search = useCallback(async (query) => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get('/customers', { params: { search: query, limit: 20 } });
      setResults(res.data.customers || res.data || []);
    } catch { setResults([]); }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), 220);
    return () => clearTimeout(timer.current);
  }, [q, search]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3 border-b">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
          <input
            autoFocus
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by customer name…"
            className="w-full h-11 pl-9 pr-9 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="cust-account-search"
          />
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">Search any customer — including paid accounts</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {!q && (
          <div className="text-center py-12 text-slate-400">
            <User size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Start typing to search</p>
          </div>
        )}
        {q && !loading && !results.length && (
          <div className="text-center py-12 text-slate-400 text-sm">No customers found for "{q}"</div>
        )}
        {results.map(c => {
          const bal = parseFloat(c.balance || 0);
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c)}
              className="w-full flex items-center justify-between px-4 py-3.5 border-b border-slate-50 hover:bg-blue-50 transition-colors text-left"
              data-testid={`cust-account-result-${c.id}`}
            >
              <div className="min-w-0 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-blue-700">{(c.name || 'C').charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                  <p className="text-[11px] text-slate-400">{c.phone || 'No phone'}</p>
                </div>
              </div>
              <div className="text-right shrink-0 ml-3 flex items-center gap-2">
                {bal > 0 ? (
                  <span className="text-sm font-bold text-red-700">{fmtPHP(bal)}</span>
                ) : (
                  <CheckCircle2 size={14} className="text-emerald-500" />
                )}
                <ChevronRight size={14} className="text-slate-300" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 3: Account View ───────────────────────────────────────────────────────
// ── Detail sheet — tap a row in Open / History / Payments to inspect it and
// reprint its document (owner ask 2026-07-28). Payment rows reprint the Proof
// of Payment (PP) via the terminal PP renderer; invoice rows reprint the
// receipt via the standard doc-type detection (same path as post-sale print).
function DetailSheet({ api, branchId, businessInfo, detail, onClose }) {
  const isPayment = detail.kind === 'payment';
  const row = detail.row;
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copies, setCopies] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [sendToPrintOpen, setSendToPrintOpen] = useState(false); // shared remote-print modal

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        if (isPayment) {
          // PP number is the payment_receipts.receipt_number
          if (!row.pp_number) {
            if (alive) { setDoc(null); setLoading(false); }
            return;
          }
          const res = await api.get(`/payment-receipts/${encodeURIComponent(row.pp_number)}`);
          if (alive) setDoc(res.data);
        } else {
          const res = await api.get(`/invoices/${row.invoice_id}`);
          if (alive) setDoc(res.data);
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'Failed to load document');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api, isPayment, row]);

  // A reprint of a receipt carrying a description or a note asks what to
  // include first — the terminal could always reprint, it just never asked
  // (owner 2026-09-23). A receipt with nothing extra prints straight through.
  const [extrasAsk, setExtrasAsk] = useState(false);
  const handleReprint = () => {
    if (!doc) return;
    if (!isPayment && hasExtras(doc)) { setExtrasAsk(true); return; }
    runReprint(null);
  };
  const runReprint = async (extras) => {
    if (!doc) return;
    setPrinting(true);
    try {
      for (let i = 0; i < copies; i++) {
        if (isPayment) {
          await PrintBridge.print({
            type: 'proof_of_payment_terminal',
            data: doc,
            format: 'thermal',
            businessInfo: businessInfo || {},
            copyNumber: i + 1,
          });
        } else {
          await PrintBridge.print({
            type: PrintBridge.getDocType(doc),
            data: doc,
            format: 'thermal',
            businessInfo: businessInfo || {},
            docCode: doc.doc_code || doc.invoice_number || '',
            copyNumber: i + 1,
            extras,
          });
        }
        if (i < copies - 1) {
          const gap = await PrintBridge.interCopyGapMs({ data: doc, format: 'thermal' });
          await new Promise(r => setTimeout(r, gap));
        }
      }
      toast.success(`Printed ${copies} ${copies === 1 ? 'copy' : 'copies'}`);
    } catch (e) {
      toast.error(e.message || 'Print failed — check printer connection');
    } finally {
      setPrinting(false);
    }
  };

  const ppLines = isPayment ? (doc?.lines || []).filter(l => parseFloat(l.amount_applied || 0) > 0) : [];
  const invItems = !isPayment ? (doc?.items || []) : [];

  return (
    <div className="absolute inset-0 bg-white z-20 flex flex-col" data-testid="cust-detail-sheet">
      {/* Header */}
      <div className="px-4 pt-3 pb-3 border-b bg-blue-50 shrink-0">
        <button onClick={onClose} className="flex items-center gap-1.5 text-blue-600 text-xs mb-1 hover:text-blue-800" data-testid="cust-detail-back">
          <ArrowLeft size={13} /> Back to account
        </button>
        <p className="font-bold text-base text-slate-900 leading-tight">
          {isPayment
            ? (row.pp_number || 'Payment')
            : (row.invoice_number || 'Invoice')}
        </p>
        <p className="text-xs text-slate-500">
          {isPayment ? 'Proof of Payment' : (row.invoice_type || 'Invoice')} · {fmtDate(row.date)}
        </p>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={22} className="animate-spin text-blue-500" />
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-12 text-red-600 text-sm flex flex-col items-center gap-2">
            <AlertTriangle size={22} />
            {error}
          </div>
        )}

        {/* Legacy payment with no PP document */}
        {!loading && !error && isPayment && !row.pp_number && (
          <div className="space-y-3">
            <div className="text-center py-6 text-slate-500 text-sm flex flex-col items-center gap-2">
              <AlertTriangle size={22} className="text-amber-500" />
              <p className="font-medium text-slate-700">No Proof of Payment document</p>
              <p className="text-xs text-slate-400 max-w-[240px]">
                This payment was recorded before PP receipts existed, so there is no document to reprint.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Date</span><span>{fmtDate(row.date)}</span></div>
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Method</span><span>{row.method || 'Cash'}</span></div>
              {row.invoice_number && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">For invoice</span><span>{row.invoice_number}</span></div>}
              {row.reference && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Ref</span><span>{row.reference}</span></div>}
              <div className="flex justify-between px-3 py-2 font-semibold"><span className="text-slate-600">Amount</span><span className="text-emerald-700">{fmtPHP(row.amount)}</span></div>
            </div>
          </div>
        )}

        {/* Proof of Payment detail */}
        {!loading && !error && isPayment && doc && (
          <div className="space-y-3">
            {(doc.status === 'voided' || doc.voided) && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 flex items-center gap-2">
                <AlertTriangle size={13} /> This Proof of Payment was VOIDED — reprint disabled.
              </div>
            )}
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Date</span><span>{fmtDate(doc.date)}</span></div>
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Method</span><span>{doc.method || 'Cash'}</span></div>
              {doc.reference && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Ref</span><span>{doc.reference}</span></div>}
              {doc.recorded_by && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Received by</span><span>{doc.recorded_by}</span></div>}
              <div className="flex justify-between px-3 py-2 font-semibold"><span className="text-slate-600">Total received</span><span className="text-emerald-700">{fmtPHP(doc.total_amount)}</span></div>
            </div>
            {ppLines.length > 0 && (
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Applied to</p>
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
                  {ppLines.map((l, i) => (
                    <div key={i} className="px-3 py-2 flex justify-between items-start">
                      <div>
                        <p className="font-medium text-slate-800">{l.invoice_number || '—'}</p>
                        <p className="text-[10px] text-slate-400">
                          {(parseFloat(l.pending_balance) || 0) <= 0.005 ? 'Settled' : `Remaining ${fmtPHP(l.pending_balance)}`}
                        </p>
                      </div>
                      <span className="text-emerald-700 font-semibold">{fmtPHP(l.amount_applied)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invoice detail */}
        {!loading && !error && !isPayment && doc && (
          <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Date</span><span>{fmtDate(doc.order_date || doc.invoice_date)}</span></div>
              {doc.due_date && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Due</span><span>{fmtDate(doc.due_date)}</span></div>}
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Status</span><span className="uppercase text-xs font-semibold">{doc.status}</span></div>
            </div>
            {invItems.length > 0 && (
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{invItems.length} item(s)</p>
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
                  {invItems.map((it, i) => (
                    <div key={i} className="px-3 py-2 flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{it.product_name}</p>
                        <p className="text-[10px] text-slate-400">{it.quantity} {it.unit || ''} × {fmtPHP(it.rate || it.price)}</p>
                      </div>
                      <span className="font-semibold shrink-0">{fmtPHP(it.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
              <div className="flex justify-between px-3 py-2 font-semibold"><span>Grand Total</span><span>{fmtPHP(doc.grand_total)}</span></div>
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Paid</span><span className="text-emerald-700">{fmtPHP(doc.amount_paid)}</span></div>
              {(parseFloat(doc.balance) || 0) > 0 && (
                <div className="flex justify-between px-3 py-2 font-semibold"><span className="text-red-600">Balance</span><span className="text-red-600">{fmtPHP(doc.balance)}</span></div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reprint footer — hidden for voided PPs (no reprinting a dead document) */}
      {!loading && !error && doc && !(isPayment && (doc.status === 'voided' || doc.voided)) && (
        <div className="border-t bg-slate-50 px-4 py-3 shrink-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                className="w-8 h-8 rounded-lg border flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                onClick={() => setCopies(c => Math.max(1, c - 1))}
                disabled={copies <= 1}
                data-testid="cust-detail-copies-minus"
              >
                <span className="text-base font-bold leading-none">−</span>
              </button>
              <span className="w-6 text-center text-sm font-bold tabular-nums">{copies}</span>
              <button
                className="w-8 h-8 rounded-lg border flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                onClick={() => setCopies(c => Math.min(5, c + 1))}
                disabled={copies >= 5}
                data-testid="cust-detail-copies-plus"
              >
                <span className="text-base font-bold leading-none">+</span>
              </button>
            </div>
            <button
              onClick={handleReprint}
              disabled={printing}
              className="px-4 py-2.5 rounded-xl bg-[#1A4D2E] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50 active:scale-95 transition-all"
              data-testid="cust-detail-reprint"
            >
              {printing ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
              {printing ? 'Printing…' : (isPayment ? 'Reprint Proof of Payment' : 'Reprint Receipt')}
            </button>
          </div>
          <button
            onClick={() => setSendToPrintOpen(true)}
            className="w-full py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold flex items-center justify-center gap-2 active:scale-95 transition-all"
            data-testid="cust-detail-remote-print"
          >
            <Send size={13} /> Send to Branch Printer
          </button>
        </div>
      )}

      <TerminalPrintExtrasSheet
        open={extrasAsk}
        doc={doc}
        onCancel={() => setExtrasAsk(false)}
        onConfirm={(extras) => { setExtrasAsk(false); runReprint(extras); }}
      />

      {/* Shared Send-to-Print modal — reprints the stored doc shown (invoice or PP) */}
      {doc && (
        <SendToPrintModal
          open={sendToPrintOpen}
          onOpenChange={setSendToPrintOpen}
          compact={true}
          axiosInstance={api}
          documentType={isPayment ? 'proof_of_payment' : PrintBridge.getDocType(doc)}
          receiverDocType={isPayment ? 'proof_of_payment' : 'invoice'}
          documentName={isPayment
            ? `Proof of Payment #${doc.receipt_number || ''}`
            : `Sales Receipt #${doc.invoice_number || ''}`}
          documentId={doc.id || ''}
          docCode={isPayment ? '' : (doc.doc_code || '')}
          referenceNumber={isPayment ? (doc.receipt_number || '') : (doc.invoice_number || '')}
          branchId={doc.branch_id || branchId || ''}
          buildHtml={(format) => isPayment
            ? PrintEngine.generateHtml({ type: 'proof_of_payment_terminal', data: doc, format, businessInfo: businessInfo || {} })
            : PrintEngine.generateHtml({ type: PrintEngine.getDocType(doc), data: doc, format, businessInfo: businessInfo || {}, docCode: doc.doc_code || doc.invoice_number || '' })}
          metadata={{ customer_name: doc.customer_name || '' }}
        />
      )}
    </div>
  );
}

function AccountView({ api, customer, branchId, businessInfo, verifierPin, onBack, onCollectPayment }) {
  const [tab, setTab]       = useState('open');
  const [period, setPeriod] = useState('30d');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printTarget, setPrintTarget] = useState(null); // { printType, copies }
  const [detail, setDetail] = useState(null); // { kind: 'payment'|'invoice', row }

  const load = useCallback(async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customer.id}/account-summary`, { params: { period } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }, [api, customer?.id, period]);

  useEffect(() => { load(); }, [load]);

  // Exact same print flow as CustomerAccountModal — copy-count picker then confirm
  const handlePrint = async () => {
    if (!data || !printTarget) return;
    setPrinting(true);
    try {
      for (let i = 0; i < (printTarget.copies || 1); i++) {
        await PrintBridge.printStatement(data, businessInfo, printTarget.printType);
        if (i < (printTarget.copies || 1) - 1) {
          await new Promise(r => setTimeout(r, 600));
        }
      }
      toast.success(`Printed ${printTarget.copies} copy(ies)`);
    } catch (e) {
      toast.error(e.message || 'Print failed');
    } finally {
      setPrinting(false);
      setPrintTarget(null);
    }
  };

  const PRINT_LABELS = {
    open_invoices: 'Open Invoices',
    history:       'History',
    payments:      'Payments',
  };

  const openInvoices = data?.open_invoices ?? [];
  const historyRows  = data?.history        ?? [];
  const payments     = data?.payments_only  ?? [];  // API returns payments_only (matches CustomerAccountModal)
  const totalBalance = parseFloat(data?.open_balance ?? data?.total_balance ?? customer.balance ?? 0);

  const TABS = [
    { key: 'open',     label: `Open (${openInvoices.length})`,   printType: 'open_invoices' },
    { key: 'history',  label: 'History',                          printType: 'history'       },
    { key: 'payments', label: 'Payments',                         printType: 'payments'      },
  ];
  const activeTab = TABS.find(t => t.key === tab);

  return (
    <div className="flex flex-col h-full relative">
      {detail && (
        <DetailSheet
          api={api}
          branchId={branchId}
          businessInfo={businessInfo}
          detail={detail}
          onClose={() => setDetail(null)}
        />
      )}
      {/* Customer header */}
      <div className="px-4 pt-3 pb-3 border-b bg-blue-50">
        <button onClick={onBack} className="flex items-center gap-1.5 text-blue-600 text-xs mb-2 hover:text-blue-800">
          <ArrowLeft size={13} /> Back to search
        </button>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center">
              <span className="text-sm font-bold text-blue-800">{(customer.name || 'C').charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <p className="font-bold text-base text-slate-900 leading-tight">{customer.name}</p>
              <p className="text-xs text-slate-500">{customer.phone || 'No phone'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-base font-bold ${totalBalance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
              {fmtPHP(totalBalance)}
            </p>
            <p className="text-[10px] text-slate-400">total balance</p>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b bg-white">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
              tab === t.key
                ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`cust-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Period selector (History + Payments tabs) */}
      {(tab === 'history' || tab === 'payments') && (
        <div className="px-4 py-2 border-b bg-slate-50 flex items-center gap-2">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">Period:</span>
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  period === p.value
                    ? 'bg-blue-700 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300'
                }`}
                data-testid={`cust-period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={22} className="animate-spin text-blue-500" />
          </div>
        )}

        {/* Open Invoices */}
        {!loading && tab === 'open' && (
          <>
            {openInvoices.length === 0 ? (
              <div className="text-center py-12 text-emerald-600">
                <CheckCircle2 size={28} className="mx-auto mb-2" />
                <p className="text-sm font-medium">No open invoices — all clear!</p>
              </div>
            ) : (
              openInvoices.map(inv => (
                <button
                  key={inv.invoice_id}
                  onClick={() => setDetail({ kind: 'invoice', row: inv })}
                  className="w-full text-left px-4 py-3 border-b border-slate-50 active:bg-blue-50 transition-colors"
                  data-testid={`cust-open-row-${inv.invoice_number}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{inv.invoice_number}</p>
                      <p className="text-[11px] text-slate-400">{fmtDate(inv.date || inv.invoice_date)} · Due {fmtDate(inv.due_date)}</p>
                      {inv.notes && <p className="text-[10px] text-slate-400 italic mt-0.5 truncate max-w-[160px]">{inv.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="text-right">
                        <p className="text-sm font-bold text-red-700">{fmtPHP(inv.balance)}</p>
                        <p className="text-[10px] text-slate-400">of {fmtPHP(inv.grand_total)}</p>
                      </div>
                      <ChevronRight size={14} className="text-slate-300 shrink-0" />
                    </div>
                  </div>
                </button>
              ))
            )}
            {openInvoices.length > 0 && totalBalance > 0 && (
              <button
                onClick={onCollectPayment}
                className="m-4 w-[calc(100%-2rem)] py-3 rounded-xl bg-emerald-700 text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-all"
                data-testid="cust-collect-payment-btn"
              >
                <Wallet size={15} /> Collect Payment — {fmtPHP(totalBalance)}
              </button>
            )}
          </>
        )}

        {/* History */}
        {!loading && tab === 'history' && (
          <>
            {historyRows.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No history for this period</div>
            ) : (
              historyRows.map((row, i) => (
                <button
                  key={i}
                  onClick={() => setDetail(row.type === 'payment'
                    ? { kind: 'payment', row }
                    : { kind: 'invoice', row })}
                  className="w-full text-left px-4 py-3 border-b border-slate-50 active:bg-blue-50 transition-colors"
                  data-testid={`cust-history-row-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-slate-900">
                          {/* PP number for payment rows; invoice/receipt number for invoice rows */}
                          {row.type === 'payment'
                            ? (row.pp_number || row.receipt_number || row.invoice_number || row.reference || '—')
                            : (row.reference || row.invoice_number || row.receipt_number || '—')
                          }
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          row.type === 'payment' ? 'bg-emerald-100 text-emerald-700'
                          : row.balance > 0 ? 'bg-red-100 text-red-700'
                          : 'bg-slate-100 text-slate-600'
                        }`}>
                          {row.type === 'payment' ? 'Payment' : row.balance > 0 ? 'Open' : 'Paid'}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400">{fmtDate(row.date || row.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${row.type === 'payment' ? 'text-emerald-700' : 'text-slate-900'}`}>
                        {row.type === 'payment' ? '+' : ''}{fmtPHP(row.amount || row.grand_total || 0)}
                      </p>
                      {row.balance > 0 && (
                        <p className="text-[10px] text-red-500">bal {fmtPHP(row.balance)}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </>
        )}

        {/* Payments */}
        {!loading && tab === 'payments' && (
          <>
            {payments.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No payments for this period</div>
            ) : (
              payments.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setDetail({ kind: 'payment', row: p })}
                  className="w-full text-left px-4 py-3 border-b border-slate-50 active:bg-blue-50 transition-colors"
                  data-testid={`cust-payment-row-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      {/* PP-MB-XXXXXX is the primary reference. Falls back to invoice
                          number so the cashier knows which invoice this paid off. */}
                      <p className="text-sm font-semibold text-slate-900">
                        {p.pp_number || p.receipt_number || p.invoice_number || p.reference || '—'}
                      </p>
                      <p className="text-[11px] text-slate-400">{fmtDate(p.date || p.created_at)} · {p.method || 'Cash'}</p>
                      {/* Show invoice reference as sub-line when PP number is displayed */}
                      {(p.pp_number || p.receipt_number) && p.invoice_number && (
                        <p className="text-[10px] text-slate-400">for {p.invoice_number}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-emerald-700">{fmtPHP(p.amount || p.total || 0)}</p>
                      <ChevronRight size={14} className="text-slate-300 shrink-0" />
                    </div>
                  </div>
                </button>
              ))
            )}
          </>
        )}
      </div>

      {/* Print footer — exact same pattern as QR scan → View Account */}
      {data && (
        <div className="border-t bg-slate-50 px-4 py-3 shrink-0">
          {printTarget ? (
            /* Copy-count picker */
            <div className="flex items-center gap-2 justify-between">
              <span className="text-xs text-slate-600 font-medium truncate">
                {PRINT_LABELS[printTarget.printType]} — copies?
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  className="w-8 h-8 rounded-lg border flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  onClick={() => setPrintTarget(p => ({ ...p, copies: Math.max(1, p.copies - 1) }))}
                  disabled={printTarget.copies <= 1}
                  data-testid="cust-print-copies-minus"
                >
                  <span className="text-base font-bold leading-none">−</span>
                </button>
                <span className="w-6 text-center text-sm font-bold tabular-nums">{printTarget.copies}</span>
                <button
                  className="w-8 h-8 rounded-lg border flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  onClick={() => setPrintTarget(p => ({ ...p, copies: Math.min(5, p.copies + 1) }))}
                  disabled={printTarget.copies >= 5}
                  data-testid="cust-print-copies-plus"
                >
                  <span className="text-base font-bold leading-none">+</span>
                </button>
                <button
                  onClick={handlePrint}
                  disabled={printing}
                  className="px-3 h-8 rounded-lg bg-[#1A4D2E] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 active:scale-95 transition-all ml-1"
                  data-testid="cust-print-confirm"
                >
                  {printing ? <Loader2 size={11} className="animate-spin" /> : <Printer size={11} />}
                  {printing ? 'Printing…' : 'Print'}
                </button>
                <button
                  className="text-xs text-slate-400 hover:text-slate-600 ml-1"
                  onClick={() => setPrintTarget(null)}
                  data-testid="cust-print-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Three named print buttons — matches QR scan account view exactly */
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-500 shrink-0 flex items-center gap-1">
                <Printer size={12} /> Print (58mm):
              </span>
              <div className="flex gap-1.5 flex-wrap justify-end">
                <button
                  onClick={() => setPrintTarget({ printType: 'open_invoices', copies: 1 })}
                  disabled={!data}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center gap-1 active:scale-95 transition-all disabled:opacity-40"
                  data-testid="cust-print-open-invoices"
                >
                  <Printer size={11} /> Open Invoices
                </button>
                <button
                  onClick={() => setPrintTarget({ printType: 'history', copies: 1 })}
                  disabled={!data}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center gap-1 active:scale-95 transition-all disabled:opacity-40"
                  data-testid="cust-print-history"
                >
                  <Printer size={11} /> History
                </button>
                <button
                  onClick={() => setPrintTarget({ printType: 'payments', copies: 1 })}
                  disabled={!data}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center gap-1 active:scale-95 transition-all disabled:opacity-40"
                  data-testid="cust-print-payments"
                >
                  <Printer size={11} /> Payments
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function TerminalCustomerAccount({ api, session, businessInfo, onClose, openCollectPayment }) {
  const [step, setStep]             = useState('pin');  // 'pin' | 'search' | 'account'
  const [verifier, setVerifier]     = useState(null);
  const [customer, setCustomer]     = useState(null);

  const handleVerified = (v) => { setVerifier(v); setStep('search'); };
  const handleSelect   = (c) => { setCustomer(c); setStep('account'); };
  const handleBack     = () => { setCustomer(null); setStep('search'); };

  // Hand off to TerminalCollectPayment for the actual payment
  const handleCollectPayment = () => {
    onClose();
    if (openCollectPayment) openCollectPayment(customer);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="terminal-customer-account-overlay">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
            <User size={16} className="text-blue-700" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {step === 'account' && customer ? customer.name : 'Customer Account'}
            </h2>
            {step !== 'pin' && (
              <p className="text-[10px] text-slate-400">
                {step === 'search' ? 'Search customer' : 'Account details'}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          data-testid="cust-account-close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden">
        {step === 'pin' && (
          <PinGate api={api} branchId={session.branchId} onVerified={handleVerified} />
        )}
        {step === 'search' && (
          <CustomerSearch api={api} onSelect={handleSelect} />
        )}
        {step === 'account' && customer && (
          <AccountView
            api={api}
            customer={customer}
            branchId={session.branchId}
            businessInfo={businessInfo}
            verifierPin={verifier?.pin}
            onBack={handleBack}
            onCollectPayment={handleCollectPayment}
          />
        )}
      </div>
    </div>
  );
}
